import React, { useEffect, useState, useRef, useCallback } from "react";
import SearchBar from "./components/SearchBar";
import PlaylistDrawer from "./components/PlaylistDrawer";
import NowPlaying from "./components/NowPlaying";
import SessionJoiner from "./components/SessionJoiner";

// Session Closed Modal Component
const SessionClosedModal = ({ reason, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
        <h2 className="text-2xl font-bold text-red-600 mb-4">Session Closed</h2>
        <p className="mb-6">{reason || "This session is no longer available."}</p>
        <p className="mb-6 text-gray-600">Please scan a new QR code or enter a new session ID to continue.</p>
        <div className="flex justify-end">
          <button 
            className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800"
            onClick={onClose}
          >
            Return to Login
          </button>
        </div>
      </div>
    </div>
  );
};

function App() {
  const [socket, setSocket] = useState(null);
  const [sessionId, setSessionId] = useState("");
  const [playlist, setPlaylist] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [audioPlayer, setAudioPlayer] = useState(null);
  const [sessionClosedReason, setSessionClosedReason] = useState(null);
  const lastScrollY = useRef(0);
  const [hideHeader, setHideHeader] = useState(false);
  
  // Add state for reconnection attempts
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  
  // Add state to track last server timestamp to prevent duplicate messages
  const lastServerTimestamp = useRef(0);

  useEffect(() => {
    // Configure Apple MusicKit
    if (window.MusicKit) {
      window.MusicKit.configure({
        developerToken: process.env.REACT_APP_APPLE_MUSIC_TOKEN,
        app: { name: "SessionTunes", build: "2.0" },
      });
    }
    
    // Initialize audio player
    const audio = new Audio();
    setAudioPlayer(audio);
    
    // Clean up audio on unmount
    return () => {
      if (audio) {
        audio.pause();
        audio.src = "";
      }
    };
  }, []);
  
  // Add scroll listener for hiding header on scroll down
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY.current && currentScrollY > 80) {
        setHideHeader(true);
      } else {
        setHideHeader(false);
      }
      lastScrollY.current = currentScrollY;
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handler for session closed events
  const handleSessionClosed = useCallback((reason) => {
    console.log(`Session closed: ${reason}`);
    setSessionClosedReason(reason);
    
    // Stop any playing audio
    if (audioPlayer) {
      audioPlayer.pause();
      audioPlayer.src = "";
    }
  }, [audioPlayer]);

  // Debug log function
  const logMessage = (direction, message) => {
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}] ${direction}: `, message);
  };
  
  // Create reconnect function
  const reconnect = useCallback((id) => {
    if (reconnectAttempts.current < maxReconnectAttempts) {
      reconnectAttempts.current++;
      console.log(`Attempting to reconnect (${reconnectAttempts.current}/${maxReconnectAttempts})...`);
      
      // Exponential backoff
      setTimeout(() => {
        joinSession(id);
      }, 1000 * Math.pow(2, reconnectAttempts.current));
    } else {
      console.log("Maximum reconnection attempts reached");
      setSessionClosedReason("Connection lost after multiple attempts. Please try again later.");
    }
  }, []);
  
  // Unified WebSocket message handler
  const handleWebSocketMessage = useCallback((event) => {
    try {
      const data = JSON.parse(event.data);
      logMessage("RECEIVE", data);
      
      // Check message timestamp to avoid processing outdated messages
      const serverTimestamp = data.serverTimestamp || 0;
      const source = data.source || "unknown";
      
      // Skip our own messages or outdated messages
      if (source === "web" || (serverTimestamp > 0 && serverTimestamp <= lastServerTimestamp.current)) {
        console.log(`Skipping message from source '${source}' with timestamp: ${serverTimestamp}`);
        return;
      }
      
      // Update timestamp for new messages
      if (serverTimestamp > lastServerTimestamp.current) {
        lastServerTimestamp.current = serverTimestamp;
      }
      
      switch (data.type) {
        case "sessionClosed":
          console.log("Session closed:", data.reason);
          handleSessionClosed(data.reason);
          break;
          
        case "playlistUpdate":
          if (Array.isArray(data.playlist)) {
            console.log("Received playlist update with", data.playlist.length, "tracks");
            setPlaylist(data.playlist);
          } else {
            console.log("Received invalid playlist data:", data.playlist);
            setPlaylist([]);
          }
          break;
          
        case "nowPlaying":
          console.log("Received nowPlaying:", data.track);
          if (data.track && data.track.id && data.track.attributes) {
            // Set current track information
            setCurrentTrack(data.track);
            setIsPlaying(true);
            
            // If track is not in playlist, request playlist refresh
            if (!playlist.some(t => t.id === data.track.id)) {
              console.log("Track not found in playlist, requesting refresh");
              if (socket && socket.readyState === WebSocket.OPEN) {
                socket.send(JSON.stringify({
                  type: "getPlaylist",
                  session: sessionId
                }));
              }
            }
          } else {
            console.log("Received invalid track data in nowPlaying:", data.track);
          }
          break;
          
        case "playbackState":
          console.log("Received playbackState:", data);
          
          // Always update playing state
          setIsPlaying(!!data.isPlaying);
          
          // Update current track if provided
          if (data.currentTrack) {
            // Find the track in the playlist
            const track = playlist.find(t => t.id === data.currentTrack);
            if (track) {
              setCurrentTrack(track);
              console.log("Updated current track to:", track.attributes.name);
            } else {
              // If track not found in playlist, refresh playlist
              console.log("Track not found in playlist, requesting refresh");
              if (socket && socket.readyState === WebSocket.OPEN) {
                socket.send(JSON.stringify({
                  type: "getPlaylist",
                  session: sessionId
                }));
              }
            }
          }
          break;
          
        case "error":
          console.log("Server error:", data.message);
          break;
          
        default:
          console.log("Unhandled message type:", data.type);
      }
    } catch (error) {
      console.error("Error processing WebSocket message:", error, event.data);
    }
  }, [handleSessionClosed, playlist, sessionId, socket]);
  
  const joinSession = useCallback((id) => {
    setIsLoading(true);
    
    try {
      // Reset timestamp tracking
      lastServerTimestamp.current = 0;
      
      // Close existing socket if there is one
      if (socket) {
        socket.close();
      }
      
      const socketUrl = process.env.REACT_APP_SOCKET_URL || "wss://sessiontunes.com/ws";
      console.log(`Connecting to WebSocket server at ${socketUrl}`);
      const ws = new WebSocket(socketUrl);
      
      ws.onopen = () => {
        console.log("WebSocket connection established with readyState:", ws.readyState);
        reconnectAttempts.current = 0; // Reset reconnect counter on success
        
        // Join the session
        const joinMsg = { type: "join", session: id };
        logMessage("SEND", joinMsg);
        ws.send(JSON.stringify(joinMsg));
        
        setIsLoading(false);
      };
      
      ws.onmessage = handleWebSocketMessage;
      
      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        setIsLoading(false);
      };
      
      // Handle unexpected closures
      ws.onclose = (event) => {
        console.log(`WebSocket connection closed: code=${event.code}, reason=${event.reason}`);
        
        // Don't attempt to reconnect if the closure was intended or session is closed
        if (!event.wasClean && sessionId === id && !sessionClosedReason) {
          console.log("Connection closed unexpectedly, attempting to reconnect...");
          reconnect(id);
        }
      };
      
      setSocket(ws);
      setSessionId(id);
      // Clear any previous session closed reason
      setSessionClosedReason(null); 
    } catch (error) {
      console.error("Error establishing WebSocket connection:", error);
      setIsLoading(false);
    }
  }, [handleWebSocketMessage, reconnect, sessionClosedReason, socket]);

  // Add heartbeat to keep connection alive
  useEffect(() => {
    if (!socket || !sessionId) return;
    
    const heartbeatInterval = setInterval(() => {
      if (socket.readyState === WebSocket.OPEN) {
        console.log("Sending heartbeat");
        socket.send(JSON.stringify({ 
          type: "test", 
          session: sessionId,
          message: "heartbeat" 
        }));
      }
    }, 30000); // Send heartbeat every 30 seconds
    
    return () => clearInterval(heartbeatInterval);
  }, [socket, sessionId]);

  // Playback control functions
  const playTrack = (track) => {
    if (!socket || socket.readyState !== WebSocket.OPEN || !sessionId) {
      console.log("Cannot play track: Socket not connected or no session ID");
      return;
    }
    
    // Send play command
    const message = {
      type: "play",
      session: sessionId,
      trackId: track.id,
      source: "web",
      clientId: "web-" + Math.random().toString(36).substring(2, 9)
    };
    
    logMessage("SEND", message);
    socket.send(JSON.stringify(message));
    
    // Update local state for immediate feedback
    setCurrentTrack(track);
    setIsPlaying(true);
  };
  
  const pausePlayback = () => {
    if (!socket || socket.readyState !== WebSocket.OPEN || !sessionId) {
      console.log("Cannot pause playback: Socket not connected or no session ID");
      return;
    }
    
    // Send pause command
    const message = {
      type: "pause",
      session: sessionId,
      source: "web",
      clientId: "web-" + Math.random().toString(36).substring(2, 9)
    };
    
    logMessage("SEND", message);
    socket.send(JSON.stringify(message));
    
    // Update local state for immediate feedback
    setIsPlaying(false);
  };
  
  const resumePlayback = () => {
    if (!socket || socket.readyState !== WebSocket.OPEN || !sessionId) {
      console.log("Cannot resume playback: Socket not connected or no session ID");
      return;
    }
    
    // Send resume command
    const message = {
      type: "resume",
      session: sessionId,
      source: "web",
      clientId: "web-" + Math.random().toString(36).substring(2, 9)
    };
    
    logMessage("SEND", message);
    socket.send(JSON.stringify(message));
    
    // Update local state for immediate feedback
    setIsPlaying(true);
  };
  
  const togglePlayPause = () => {
    if (isPlaying) {
      pausePlayback();
    } else {
      resumePlayback();
    }
  };
  
  const addToPlaylist = (track) => {
    if (!socket || socket.readyState !== WebSocket.OPEN || !sessionId) {
      console.log("WebSocket not connected. Unable to add track.");
      return;
    }
    
    // Send add track command
    const message = {
      type: "add", 
      session: sessionId, 
      track
    };
    
    logMessage("SEND", message);
    socket.send(JSON.stringify(message));
  };
  
  const removeFromPlaylist = (track) => {
    if (!track || !track.id) {
      console.log("Invalid track to remove:", track);
      return;
    }
    
    if (!socket || socket.readyState !== WebSocket.OPEN || !sessionId) {
      console.log("Cannot remove track: Socket not connected or no session ID");
      return;
    }
    
    // Send remove track command
    const message = {
      type: "remove", 
      session: sessionId, 
      trackId: String(track.id)
    };
    
    logMessage("SEND", message);
    socket.send(JSON.stringify(message));
  };

  const leaveSession = () => {
    if (socket) {
      socket.close();
    }
    
    // Stop any playing audio
    if (audioPlayer) {
      audioPlayer.pause();
      audioPlayer.src = "";
    }
    
    setSocket(null);
    setSessionId("");
    setPlaylist([]);
    setCurrentTrack(null);
    setSessionClosedReason(null);
    reconnectAttempts.current = 0;
  };

  // If no session, show session joiner
  if (!sessionId) {
    return (
      <div className="container">
        <SessionJoiner onJoin={joinSession} isLoading={isLoading} />
      </div>
    );
  }

  return (
    <div className="container">
      <header className={`app-header ${hideHeader ? 'hidden' : ''}`}>
        <div className="app-logo">
         filtox.
        </div>
        
        <div className="session-controls">
          <div className="session-info">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" 
                stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" 
                stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" 
                stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" 
                stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Session: <span className="session-id">{sessionId}</span>
          </div>
          
          <button 
            className="btn-icon" 
            onClick={() => setDrawerOpen(!drawerOpen)} 
            aria-label="Toggle playlist"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18V5L21 3V16" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="6" cy="18" r="3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="18" cy="16" r="3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          
          <button 
            className="btn-icon" 
            onClick={leaveSession} 
            aria-label="Leave session"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" 
                stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 17L21 12L16 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M21 12H9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </header>

      <main className="main-content">
        <SearchBar 
          onAddTrack={addToPlaylist} 
          onPlayTrack={playTrack}
        />
      </main>
      
      {/* Always render a placeholder for the now playing component to maintain layout */}
      <div className={`now-playing-placeholder ${!currentTrack ? 'hidden' : ''}`} style={{ height: '80px' }}></div>
      
      <PlaylistDrawer 
        open={drawerOpen} 
        tracks={playlist} 
        onClose={() => setDrawerOpen(false)} 
        currentTrack={currentTrack}
        isPlaying={isPlaying}
        onRemoveTrack={removeFromPlaylist}
        onPlayTrack={playTrack}
      />
      
      {/* Now Playing bar - positioned last in the DOM for proper stacking */}
      {currentTrack && (
        <NowPlaying 
          track={currentTrack} 
          audioPlayer={audioPlayer}
          isPlaying={isPlaying}
          onTogglePlayPause={togglePlayPause}
        />
      )}
      
      {/* Connection status indicators */}
      {socket && socket.readyState !== WebSocket.OPEN && !sessionClosedReason && (
        <div className="fixed top-4 right-4 bg-yellow-500 text-white px-4 py-2 rounded shadow-lg">
          Reconnecting...
        </div>
      )}
      
      {/* Session closed modal */}
      {sessionClosedReason && (
        <SessionClosedModal 
          reason={sessionClosedReason}
          onClose={leaveSession}
        />
      )}
    </div>
  );
}

export default App;